import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from '@/lib/auth';
import { RequireUser, RequireReseller, RequireAdmin } from '@/components/RequireAuth';
import { ImpersonationBanner } from '@/components/ImpersonationBanner';

import LandingPage from '@/pages/LandingPage';
import PlatformDetailPage from '@/pages/PlatformDetailPage';
import PolicyPage from '@/pages/PolicyPage';
import LoginPage from '@/pages/LoginPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import RegisterPage from '@/pages/RegisterPage';
import ResellerLoginPage from '@/pages/ResellerLoginPage';
import AdminLoginPage from '@/pages/AdminLoginPage';

import UserDashboardPage from '@/pages/UserDashboardPage';
import RechargePage from '@/pages/RechargePage';
import PaymentReturnPage from '@/pages/PaymentReturnPage';

import ResellerDashboard from '@/pages/reseller/ResellerDashboard';
import ResellerRecharge from '@/pages/reseller/Recharge';
import ResellerHistory from '@/pages/reseller/History';
import ResellerStocks from '@/pages/reseller/Stocks';
import ResellerAllocations from '@/pages/reseller/Allocations';
import ResellerPayments from '@/pages/reseller/Payments';
import ResellerLayout from '@/components/layouts/ResellerLayout';
import ResellerSubAgents from '@/pages/reseller/SubAgents';
import { AdminShell } from '@/components/layout/AdminShell';

import AdminDashboardPage from '@/pages/admin/AdminDashboardPage';
import AdminUsersPage from '@/pages/admin/AdminUsersPage';
import AdminPlansPage from '@/pages/admin/AdminPlansPage';
import AdminPaymentsPage from '@/pages/admin/AdminPaymentsPage';
import AdminLogsPage from '@/pages/admin/AdminLogsPage';
import AdminPlatformsPage from '@/pages/admin/AdminPlatformsPage';
import AdminSettingsPage from '@/pages/admin/AdminSettingsPage';
import AdminPoliciesPage from '@/pages/admin/AdminPoliciesPage';

import AdminOverview from '@/pages/admin/AdminDashboard';
import AdminResellers from '@/pages/admin/Resellers';
import AdminResellerDetail from '@/pages/admin/ResellerDetail';
import AdminRecharges from '@/pages/admin/AllRecharges';
import AdminNoblePacks from '@/pages/admin/NoblePacks';
import AdminSessionSettings from '@/pages/admin/SessionSettings';
import AdminAlerts from '@/pages/admin/Alerts';
import AdminAccountPage from '@/pages/admin/AdminAccount';
import AdminPaymentGateways from '@/pages/admin/AdminPaymentGateways';
import AdminOtpConfig from '@/pages/admin/AdminOtpConfig';
import AdminStockOrders from '@/pages/admin/StockOrders';
import AdminUserStockOrders from '@/pages/admin/UserStockOrders';
import AdminBlocklist from '@/pages/admin/AdminBlocklist';
import AdminMultiAccounts from '@/pages/admin/AdminMultiAccounts';
import AdminBannersPage from '@/pages/admin/AdminBannersPage';
import PromotePage from '@/pages/PromotePage';

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster position="top-right" richColors />
                <ImpersonationBanner />
                <div className="App">
                    <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/platforms/:platform" element={<PlatformDetailPage />} />
                        <Route path="/policies/:slug" element={<PolicyPage />} />
                        <Route path="/policies" element={<Navigate to="/policies/refund" replace />} />

                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/reseller/login" element={<ResellerLoginPage />} />
                        <Route path="/admin/login" element={<AdminLoginPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

                        <Route path="/dashboard" element={<RequireUser><UserDashboardPage /></RequireUser>} />
                        <Route path="/recharge" element={<RequireUser><RechargePage /></RequireUser>} />
                        <Route path="/promote" element={<RequireUser><PromotePage /></RequireUser>} />
                        <Route path="/payment/return" element={<RequireUser><PaymentReturnPage /></RequireUser>} />

                        <Route path="/reseller" element={<RequireReseller><ResellerLayout /></RequireReseller>}>
                            <Route index element={<ResellerDashboard />} />
                            <Route path="recharge" element={<ResellerRecharge />} />
                            <Route path="stocks" element={<ResellerStocks />} />
                            <Route path="allocations" element={<ResellerAllocations />} />
                            <Route path="payments" element={<ResellerPayments />} />
                            <Route path="history" element={<ResellerHistory />} />
                            <Route path="subagents" element={<ResellerSubAgents />} />
                        </Route>

                        <Route path="/admin" element={<RequireAdmin><AdminShell title="Sales Overview"><AdminOverview /></AdminShell></RequireAdmin>} />
                        <Route path="/admin/overview" element={<Navigate to="/admin" replace />} />
                        <Route path="/admin/users" element={<RequireAdmin><AdminUsersPage /></RequireAdmin>} />
                        <Route path="/admin/resellers" element={<RequireAdmin><AdminShell title="Resellers"><AdminResellers /></AdminShell></RequireAdmin>} />
                        <Route path="/admin/resellers/:id" element={<RequireAdmin><AdminShell title="Reseller Detail"><AdminResellerDetail /></AdminShell></RequireAdmin>} />
                        <Route path="/admin/recharges" element={<Navigate to="/admin/recharge-logs" replace />} />
                        <Route path="/admin/noble-packs" element={<RequireAdmin><AdminShell title="Noble Packs"><AdminNoblePacks /></AdminShell></RequireAdmin>} />
                        <Route path="/admin/plans" element={<RequireAdmin><AdminPlansPage /></RequireAdmin>} />
                        <Route path="/admin/payments" element={<RequireAdmin><AdminPaymentsPage /></RequireAdmin>} />
                        <Route path="/admin/stock-orders" element={<RequireAdmin><AdminShell title="Stock Orders"><AdminStockOrders /></AdminShell></RequireAdmin>} />
                        <Route path="/admin/user-stock-orders" element={<RequireAdmin><AdminShell title="User Stock Orders"><AdminUserStockOrders /></AdminShell></RequireAdmin>} />
                        <Route path="/admin/banners" element={<RequireAdmin><AdminShell title="Host Banners"><AdminBannersPage /></AdminShell></RequireAdmin>} />
                        <Route path="/admin/multi-accounts" element={<RequireAdmin><AdminShell title="Multi-account Review"><AdminMultiAccounts /></AdminShell></RequireAdmin>} />
                        <Route path="/admin/blocklist" element={<RequireAdmin><AdminShell title="Blocklist"><AdminBlocklist /></AdminShell></RequireAdmin>} />
                        <Route path="/admin/recharge-logs" element={<RequireAdmin><AdminLogsPage /></RequireAdmin>} />
                        <Route path="/admin/platforms" element={<RequireAdmin><AdminPlatformsPage /></RequireAdmin>} />
                        <Route path="/admin/session" element={<RequireAdmin><AdminShell title="Reseller Session"><AdminSessionSettings /></AdminShell></RequireAdmin>} />
                        <Route path="/admin/settings" element={<RequireAdmin><AdminSettingsPage /></RequireAdmin>} />
                        <Route path="/admin/payment-gateways" element={<RequireAdmin><AdminPaymentGateways /></RequireAdmin>} />
                        <Route path="/admin/policies" element={<RequireAdmin><AdminPoliciesPage /></RequireAdmin>} />
                        <Route path="/admin/alerts" element={<RequireAdmin><AdminShell title="Alerts"><AdminAlerts /></AdminShell></RequireAdmin>} />
                        <Route path="/admin/otp-config" element={<RequireAdmin><AdminShell title="OTP & Verification"><AdminOtpConfig /></AdminShell></RequireAdmin>} />
                        <Route path="/admin/account" element={<RequireAdmin><AdminShell title="Account"><AdminAccountPage /></AdminShell></RequireAdmin>} />


                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </div>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
